import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js'
import {
  Drawer,
  Box,
  Typography,
  Button,
  IconButton,
  Alert,
  Paper,
  CircularProgress,
  Divider,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useStripePaymentContext } from '@/view/providers/StripePaymentProvider'
import { routes } from '@/routes'
import { PaymentIntent } from '@stripe/stripe-js'
import { IntentTypes } from '@/@types/stripe'
import { toDollars } from '@/utils/stripeUtils'
import log from 'loglevel'
import { useLazyQuery } from '@apollo/client'
import { GetQuotePaymentInfoDocument } from '@/@types/generated/graphql'
import { PaymentInfoDisplay } from '../atoms/PaymentInfoDisplay'
import { removeSessionItem, SessionStorageKeys } from '@/utils/sessionStorage'
import { formattedAmount } from '@/utils/stripeUtils'

const paymentElementOptions = {
  currency: 'usd',
  mode: 'setup',
  // style: {
  //   base: {
  //     fontSize: '16px',
  //     color: '#424770',
  //     fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  //     '::placeholder': {
  //       color: '#aab7c4',
  //     },
  //     padding: '12px',
  //   },
  //   invalid: {
  //     color: '#9e2146',
  //   },
  // },
}

export const PaymentDrawer: React.FC = () => {
  const stripe = useStripe()
  const { legalMatterKey } = useParams()
  const elements = useElements()
  const {
    isDrawerOpen,
    closeDrawer,
    paymentIntentClientSecret,
    setupIntentClientSecret,
    retrievedPaymentMethodId,
    currentIntent,
    isProcessing,
    fetchPaymentIntentDetails,
  } = useStripePaymentContext()

  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [intentDetails, setIntentDetails] = useState<PaymentIntent | null>(null)
  const { setupIntent, quoteKeyToPay } = useStripePaymentContext()
  const [fetchPaymentInfo, { data: paymentData }] = useLazyQuery(
    GetQuotePaymentInfoDocument,
  )

  const getIntentDetails = async (
    paymentIntentClientSecret: string,
  ): Promise<void> => {
    if (paymentIntentClientSecret) {
      const intent = await fetchPaymentIntentDetails(paymentIntentClientSecret)
      setIntentDetails(intent)
    }
  }

  useEffect(() => {
    if (paymentIntentClientSecret) {
      void getIntentDetails(paymentIntentClientSecret)
    }
  }, [paymentIntentClientSecret])

  useEffect(() => {
    fetchPaymentInfo({
      variables: { quoteKey: quoteKeyToPay, setupIntentId: setupIntent },
    })
  }, [setupIntent, quoteKeyToPay])

  const cardInfo = paymentData?.getQuotePaymentInfo

  // clear the sesionStorage after query gets done and Card info gets retrived
  if (!!cardInfo) {
    removeSessionItem(SessionStorageKeys.QuoteKey)
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!stripe || !elements) return

    setProcessing(true)
    setError(null)

    let result

    try {
      if (currentIntent === IntentTypes.PAYMENT) {
        if (!paymentIntentClientSecret || !retrievedPaymentMethodId) return
        result = await stripe.confirmPayment({
          clientSecret: paymentIntentClientSecret,
          confirmParams: {
            payment_method: retrievedPaymentMethodId,
            return_url: `${window.location.origin}${routes.legalMatterDetail.param({ legalMatterKey })}`,
          },
          redirect: 'if_required',
        })
      } else {
        if (!setupIntentClientSecret) return

        const { error: elementsSubmitError } = await elements.submit()
        if (elementsSubmitError) {
          setError(elementsSubmitError?.message ?? '')
          return
        }
        result = await stripe.confirmSetup({
          clientSecret: setupIntentClientSecret,
          elements: elements,
          confirmParams: {
            return_url: `${window.location.origin}${routes.legalMatterDetail.param({ legalMatterKey })}`,
          },
        })
      }

      if (result.error) {
        setError(result.error.message || 'An error occurred')
      } else {
        closeDrawer()
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'An unexpected error occurred',
      )
    } finally {
      setProcessing(false)
    }
  }

  const isLoading = processing || isProcessing
  const drawerTitle =
    currentIntent === IntentTypes.PAYMENT
      ? 'Complete Payment'
      : 'Setup Payment Method'
  const submitButtonText =
    currentIntent === IntentTypes.PAYMENT ? 'Pay Now' : 'Setup Payment Method'

  return (
    <Drawer
      anchor="right"
      open={isDrawerOpen}
      onClose={closeDrawer}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 400 } },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            p: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="h6" component="h2">
            {drawerTitle}
          </Typography>
          <IconButton onClick={closeDrawer} edge="end" aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        <Box sx={{ flex: 1, p: 3 }}>
          <Box component="form" onSubmit={handleSubmit}>
            {!intentDetails && (
              <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
                Card information:
              </Typography>
            )}
            {intentDetails && (
              <>
                <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
                  By clicking Pay Now you agree to pay the total listed below
                  using the selected card.
                </Typography>
              </>
            )}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 3,
                '& .StripeElement': {
                  padding: '12px 0',
                },
                '& .StripeElement--focus': {
                  outline: 'none',
                },
              }}
            >
              {!intentDetails && <PaymentElement />}
              {intentDetails && (
                <TableContainer component={Paper}>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell
                          colSpan={2}
                          sx={{ backgroundColor: '#f5f5f5' }}
                        >
                          <Typography variant="caption">
                            {intentDetails.description}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <Typography variant="caption">Total: </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography>
                            ${formattedAmount(toDollars(intentDetails.amount))}{' '}
                            {intentDetails.currency.toUpperCase()}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {!!cardInfo && !!intentDetails && (
                <PaymentInfoDisplay
                  type={cardInfo?.type}
                  endNum={cardInfo?.endNum}
                  month={cardInfo?.month}
                  year={cardInfo?.year}
                />
              )}
            </Paper>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={!stripe || isLoading}
              startIcon={isLoading ? <CircularProgress size={20} /> : null}
              sx={{
                mt: 2,
                py: 1.5,
                textTransform: 'none',
                fontSize: '16px',
              }}
            >
              {isLoading ? 'Processing...' : submitButtonText}
            </Button>
          </Box>
        </Box>

        <Box sx={{ p: 3, pt: 0 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            🔒 Your payment information is secure and encrypted
          </Typography>
        </Box>
      </Box>
    </Drawer>
  )
}
